import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Upload, FileText, Trash2, CalendarClock } from "lucide-react";

const REQUIRED_DOCS = ["Aadhaar", "PAN", "Bank Details", "Address Proof", "Qualification Proof", "Photograph", "Offer/Joining Document", "Last Salary Slip", "Last Experience Letter"];
const EXPIRY_DOCS = ["Police Verification", "Medical Fitness Certificate", "Insurance"];

export default function EmployeeProfile() {
  const { id } = useParams();
  const [emp, setEmp] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState(REQUIRED_DOCS[0]);
  const [expiryDate, setExpiryDate] = useState("");

  const load = async () => {
    const { data } = await api.get(`/employees/${id}`);
    setEmp(data);
  };

  useEffect(() => { load(); }, [id]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = new URLSearchParams({
        entity: "employee",
        entity_id: id,
        doc_type: docType,
        ...(expiryDate ? { expires_at: new Date(expiryDate).toISOString() } : {}),
      }).toString();
      await api.post(`/files/upload?${qs}`, fd);
      toast.success("Uploaded — documentation status auto-updated");
      setExpiryDate("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = async (fid) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/files/${fid}`);
    load();
  };

  const updateSatisfaction = async (v) => {
    await api.patch(`/employees/${id}`, { satisfaction_status: v });
    toast.success("Updated");
    load();
  };

  if (!emp) return <div className="text-slate-500 label-eyebrow">Loading profile…</div>;

  const docPill = { Complete: "rag-green", Partial: "rag-amber", Pending: "rag-red" }[emp.documentation_status];

  return (
    <div className="space-y-6" data-testid="employee-profile-page">
      <Link to="/employees" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[var(--yc-navy)]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to employees
      </Link>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 flex items-start gap-6">
        <div className="h-16 w-16 rounded-full bg-[var(--yc-navy)] text-[var(--yc-gold)] flex items-center justify-center font-heading font-bold text-2xl">
          {emp.name?.charAt(0)}
        </div>
        <div className="flex-1">
          <div className="label-eyebrow">{emp.designation}</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">{emp.name}</h1>
          <div className="text-slate-600 mt-1 text-sm">{emp.department} · {emp.branch} · Joined {emp.joining_date?.slice(0, 10)} · {emp.tenure_days}d tenure</div>
          <div className="flex gap-2 mt-3">
            <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${docPill}`}>
              Docs: {emp.documentation_status}
            </span>
            {emp.retention_risk && (
              <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
                emp.retention_risk === "High" ? "rag-red" : emp.retention_risk === "Medium" ? "rag-amber" : "rag-green"
              }`}>
                Risk: {emp.retention_risk}
              </span>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" data-testid="employee-tabs">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Review history</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Panel title="Contact">
              <Row label="Phone" value={emp.phone} />
              <Row label="Emergency contact" value={emp.emergency_contact_name} />
              <Row label="Emergency phone" value={emp.emergency_contact_phone} />
              <Row label="Relation" value={emp.emergency_contact_relation} />
            </Panel>
            <Panel title="Engagement">
              <div className="py-2 flex items-center justify-between">
                <div className="text-sm text-slate-600">Satisfaction status</div>
                <Select value={emp.satisfaction_status} onValueChange={updateSatisfaction}>
                  <SelectTrigger className="w-48" data-testid="satisfaction-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Satisfied","Neutral","Unsatisfied","Not Checked Yet"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Row label="Last check-in" value={emp.last_check_in_date?.slice(0,10) || "—"} />
              <Row label="Employment status" value={emp.employment_status} />
              <Row label="HR feedback" value={emp.hr_feedback || "—"} />
            </Panel>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-eyebrow">Documentation</div>
                <div className="font-heading font-semibold text-[var(--yc-navy)]">Required documents auto-sync with status</div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REQUIRED_DOCS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    {EXPIRY_DOCS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {EXPIRY_DOCS.includes(docType) && (
                  <div className="flex items-center gap-2 border border-slate-200 rounded-md pl-2 pr-1 py-1 bg-slate-50">
                    <CalendarClock className="h-3.5 w-3.5 text-[var(--yc-gold)]" />
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">Expires</span>
                    <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="h-7 w-40 border-0 bg-transparent p-1 text-xs" data-testid="doc-expiry-input" />
                  </div>
                )}
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--yc-navy)] text-white text-sm hover:bg-[var(--yc-navy-soft)] cursor-pointer">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload"}
                  <input type="file" className="hidden" onChange={upload} data-testid="doc-upload-input" accept=".pdf,.png,.jpg,.jpeg" />
                </label>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {REQUIRED_DOCS.map((d) => {
                const uploaded = emp.documents?.find((f) => f.doc_type === d);
                const marked = emp.doc_marks?.[d];
                const done = uploaded || marked;
                return (
                  <div key={d} className={`p-4 rounded-md border ${done ? "border-emerald-200 bg-emerald-50/40" : "border-dashed border-slate-300"}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-[var(--yc-navy)]">{d}</div>
                        <div className="text-xs text-slate-500">{uploaded ? uploaded.original_filename : marked ? "Marked submitted (no file)" : "Pending"}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {uploaded && (
                          <>
                            <a href={`${api.defaults.baseURL}/files/${uploaded.id}/download?auth=${localStorage.getItem("yc_token")}`} target="_blank" rel="noreferrer" className="text-xs text-[var(--yc-emerald)] hover:underline">View</a>
                            <button onClick={() => removeFile(uploaded.id)} className="text-slate-400 hover:text-[var(--yc-rose)]"><Trash2 className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                        {!uploaded && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await api.patch(`/employees/${emp.id}`, { doc_marks: { ...(emp.doc_marks || {}), [d]: !marked } });
                                load();
                              } catch { /* role not allowed */ }
                            }}
                            className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full border transition-colors ${marked ? "rag-green border-transparent" : "border-slate-300 text-slate-500 hover:border-[var(--yc-emerald)] hover:text-[var(--yc-emerald)]"}`}
                            data-testid={`doc-mark-${d.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                          >
                            {marked ? "Submitted" : "Mark submitted"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {emp.documents?.filter((f) => !REQUIRED_DOCS.includes(f.doc_type)).length > 0 && (
              <div className="mt-6">
                <div className="label-eyebrow mb-2">Additional files</div>
                <div className="flex flex-wrap gap-2">
                  {emp.documents.filter((f) => !REQUIRED_DOCS.includes(f.doc_type)).map((f) => (
                    <div key={f.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-slate-50">
                      <FileText className="h-3.5 w-3.5 text-slate-500" />
                      <a href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${localStorage.getItem("yc_token")}`} target="_blank" rel="noreferrer" className="text-[var(--yc-navy)] hover:underline">{f.original_filename}</a>
                      <button onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-[var(--yc-rose)]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="reviews" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                  <th className="data-cell">Date</th>
                  <th className="data-cell">Reviewer</th>
                  <th className="data-cell">Performance</th>
                  <th className="data-cell">Engagement</th>
                  <th className="data-cell">Risk</th>
                  <th className="data-cell">Next Review</th>
                </tr>
              </thead>
              <tbody>
                {(!emp.reviews || emp.reviews.length === 0) && (
                  <tr><td colSpan={6} className="data-cell text-center text-slate-500 py-8">No reviews yet.</td></tr>
                )}
                {emp.reviews?.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="data-cell">{r.review_date?.slice(0,10)}</td>
                    <td className="data-cell text-slate-600">{r.reviewer}</td>
                    <td className="data-cell font-mono">{r.performance_score}/5</td>
                    <td className="data-cell font-mono">{r.engagement_score}/5</td>
                    <td className="data-cell">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
                        r.retention_risk === "High" ? "rag-red" : r.retention_risk === "Medium" ? "rag-amber" : "rag-green"
                      }`}>{r.retention_risk}</span>
                    </td>
                    <td className="data-cell text-slate-500">{r.next_review_date?.slice(0,10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <div className="label-eyebrow mb-3">{title}</div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="py-2 flex justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-[var(--yc-navy)] font-medium">{value || "—"}</span>
    </div>
  );
}
